import { Box } from '@chakra-ui/react';
import React from 'react';

import { TextField } from 'Containers/App/types';
import { AudioConfigTooltipContentfulPaths } from 'Containers/ProfilePage/ProfileAudio';
import { NetworkPreference, ProfileAudio, ProviderPreferenceCard } from 'ContentfulDefaults/types/profile';
import { useTextContext } from 'Contexts/textContext';
import { Icon, Paragraph, Tooltip } from 'DesignLibrary/atoms';
import { AudioTooltip } from 'DesignLibrary/atoms/AudioTooltip';
import ToggleList from 'DesignLibrary/atoms/ToggleList';
import { useViewport } from 'DesignLibrary/context';
import Text from 'Shared/Text';

import { ProviderPreferenceQuestionWrapper } from './styled';
import { NetworkPreferenceSurveyAnswer, NetworkPreferenceSurveyQuestion } from '../../types';

export interface ProviderPreferenceQuestionProps {
  surveyQuestion: NetworkPreferenceSurveyQuestion;
  answer: NetworkPreferenceSurveyAnswer | null;
  isProfileAudioEnabled?: boolean;
  handleChange: (id: number, value: NetworkPreferenceSurveyAnswer) => void;
}

// A question id is a contract comm-api shares with two places nothing here can reach:
// its wording and tooltip live in Contentful under provider_preferences.questions, and
// its narration in the map below. Change an id on the comm-api side and both need the
// same change — an unrecognised id still renders, in English and with no audio, which
// is the safety net rather than the intent.
//
// Each question is narrated by its own recording, so a question id with nothing
// recorded for it keeps the written tooltip instead.
const QUESTION_AUDIO_PATHS: Record<number, AudioConfigTooltipContentfulPaths> = {
  11: AudioConfigTooltipContentfulPaths.FewerInNetworkDoctorsAudioTooltip,
  12: AudioConfigTooltipContentfulPaths.NationalCoverageNeedAudioTooltip,
  13: AudioConfigTooltipContentfulPaths.ReferralRequirementAudioTooltip,
};

// A question id that content has no wording for yet falls back to the text comm-api
// sent, which is untranslated — so a new id shows English until Contentful catches up.
export const ProviderPreferenceQuestion = ({
  surveyQuestion,
  answer,
  isProfileAudioEnabled = false,
  handleChange,
}: ProviderPreferenceQuestionProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { device } = useViewport();
  const { id, question } = surveyQuestion;

  const card = retrieveContentfulData<ProviderPreferenceCard>('profile_section_addtl.provider_preferences');
  const questionContent = card?.questions?.[id] as NetworkPreference | undefined;

  const questionField = `profile_section_addtl.provider_preferences.questions.${id}.question` as TextField;
  const tooltipLabel = questionContent?.tooltip?.name;

  const audioPath = QUESTION_AUDIO_PATHS[id];
  const audioConfig = audioPath ? retrieveContentfulData<ProfileAudio>(audioPath) : undefined;
  const shouldUseAudioTooltip = isProfileAudioEnabled && !!audioConfig?.audio_file_url;

  const yesLabel = card?.yes_label || 'Yes';
  const noLabel = card?.no_label || 'No';

  const groupName = `network-question-id-${id}`;

  const prompt = (
    <Paragraph id={groupName} size="normal" color="--text-gray" weight="normal">
      {questionContent ? <Text field={questionField} /> : question}
      {!shouldUseAudioTooltip && tooltipLabel?.text && (
        <Tooltip id={`tooltip-${groupName}`} data-testid={`tooltip-${groupName}`} label={tooltipLabel}>
          <span className="tip">
            <Icon type="Question" color="--text-black" />
          </span>
        </Tooltip>
      )}
    </Paragraph>
  );

  return (
    <ProviderPreferenceQuestionWrapper>
      <div className="question">
        {shouldUseAudioTooltip && audioConfig ? (
          <Box pos="relative">
            <Box pos="absolute" top={-2} left={-12}>
              <AudioTooltip
                data-testid={`audio-tooltip-${groupName}`}
                urlToAudio={audioConfig.audio_file_url}
                urlToClosedCaptionVtt={audioConfig.vtt_transcript_file_url}
              />
            </Box>
            {prompt}
          </Box>
        ) : (
          prompt
        )}
        <ToggleList
          layout="row"
          groupName={groupName}
          label={questionContent?.question || question}
          options={[
            { option: yesLabel, value: 'yes' },
            { option: noLabel, value: 'no' },
          ]}
          required
          color="--colors-gray-black"
          handleChange={(e) => handleChange(id, e.target.value as NetworkPreferenceSurveyAnswer)}
          selected={answer || ''}
          stretch={device === 'mobile'}
        />
      </div>
    </ProviderPreferenceQuestionWrapper>
  );
};
